import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import DashboardLayout from '../layouts/DashboardLayout';
import RecipeCard from '../components/RecipeCard';
import RecipeModalGateway from '../components/RecipeModalGateway';
import RecipeMenuGroup from '../components/RecipeMenuGroup';
import SavedValueWidget from '../components/SavedValueWidget';
import HealthPlusActivationModal from '../components/HealthPlusActivationModal';
import PremiumPurchaseModal from '../components/PremiumPurchaseModal';
import HealthPlusMenuCard from '../components/HealthPlusMenuCard';
import HealthMedicalNotice from '../components/HealthMedicalNotice';
import BabyModal from '../components/BabyModal';
import MenuCompletionBanner from '../components/MenuCompletionBanner';
import useBabyPrompt from '../hooks/useBabyPrompt';
import useMenuCompletionState from '../hooks/useMenuCompletionState';
import { API_BASE_URL } from '../config';
import Swal from 'sweetalert2';
import useMenuApproval from '../hooks/useMenuApproval';
import { hydrateRecipeForModal } from '../utils/recipeModalData';
import { resolveClientMealSlot, mealSlotLabel } from '../utils/mealSlotUi';
import {
    buildMissingShoppingItems,
    getRecipeMissingIngredients,
    normalizeRecipeIngredientName
} from '../utils/recipeAvailability';

export default function SmartSearchPage({ isDarkMode, toggleDarkMode }) {
    const navigate = useNavigate();
    const location = useLocation();
    const [loading, setLoading] = useState(true);
    const [recipes, setRecipes] = useState([]);
    const [structuredMenu, setStructuredMenu] = useState(null);
    const [matrix, setMatrix] = useState(null);
    const [savedValue, setSavedValue] = useState(0);
    const [availableComponents, setAvailableComponents] = useState([]);
    const [userProfile, setUserProfile] = useState(null);
    const [selectedCategories, setSelectedCategories] = useState(['main', 'soup', 'salad']);
    const [preferencesChanged, setPreferencesChanged] = useState(false);
    const [markingAll, setMarkingAll] = useState(false);
    const [selectedRecipeNames, setSelectedRecipeNames] = useState([]);
    const [viewMode, setViewMode] = useState('smart_menu'); // 'smart_menu' | 'browse_all'
    const [browseRecipes, setBrowseRecipes] = useState([]);
    const [browseLoading, setBrowseLoading] = useState(false);
    const [planningConfig, setPlanningConfig] = useState(null);
    const [onboardingGate, setOnboardingGate] = useState(null);
    const [gateState, setGateState] = useState('');
    const [blockedReasons, setBlockedReasons] = useState([]);
    const [mealSlotActivation, setMealSlotActivation] = useState(null);
    const [mealSlotTrigger, setMealSlotTrigger] = useState(null);
    const [recentWarmup, setRecentWarmup] = useState(null);
    const [mealIntentPush, setMealIntentPush] = useState(null);
    const [smartSearchNotice, setSmartSearchNotice] = useState(null);

    // [V12.16] Pagination State
    const [currentPage, setCurrentPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const PAGE_SIZE = 12;
    const composeBrowseRows = (recipes = []) => {
        const result = [];
        const normal = [];
        const paired = [];

        recipes.forEach((recipe) => {
            if (Array.isArray(recipe?.suggestedPairings) && recipe.suggestedPairings.length > 0) {
                paired.push(recipe);
            } else {
                normal.push(recipe);
            }
        });

        let n = 0;
        let p = 0;

        while (n < normal.length || p < paired.length) {
            if (p < paired.length && n + 1 < normal.length) {
                result.push(normal[n++]);
                result.push(paired[p++]);
                result.push(normal[n++]);
            } else if (p < paired.length) {
                result.push(paired[p++]);
            } else {
                result.push(normal[n++]);
            }
        }

        return result;
    };

    const getAuthHeaders = (extra = {}) => {
        const token = localStorage.getItem('authToken');
        if (token) return { Authorization: `Bearer ${token}`, ...extra };
        return { ...extra };
    };

    const updateSmartSearchNotice = (meta = null) => {
        const notice = meta?.userNotice || null;
        if (meta?.recentHistoryRelaxed && notice?.type === 'recent_history_inventory_relaxation') {
            setSmartSearchNotice({
                ...notice,
                message: 'Evdeki malzemelerle şu an yakın zamanda yediğiniz yemekler tekrar oluşabiliyor. Daha çeşitli öneriler için sistem üzerinden alışveriş yaparak mutfağınızdaki ürünleri tazeleyin.'
            });
            return;
        }
        setSmartSearchNotice(null);
    };

    const currentMealSlot = (() => {
        try {
            const params = new URLSearchParams(location.search || '');
            const requestedMealSlot = String(params.get('mealSlot') || '').trim().toLowerCase();
            if (requestedMealSlot === 'breakfast' || requestedMealSlot === 'lunch' || requestedMealSlot === 'dinner') {
                return requestedMealSlot;
            }
        } catch (_error) {
            // Fallback to time-based slot resolution below.
        }
        return resolveClientMealSlot();
    })();
    const currentMealLabel = mealSlotLabel(currentMealSlot);
    const { approveMenu, isApproving } = useMenuApproval({
        onSuccess: async () => {
            setSelectedRecipeNames([]);
            const completion = await refreshMenuCompletionState({ mealSlot: currentMealSlot });
            if (!completion?.mealCompletionState?.slots?.[currentMealSlot] || completion?.mealCompletionState?.slots?.[currentMealSlot]?.status !== 'completed') {
                handleInventorySearch(selectedCategories, 1);
            }
        }
    });

    // Dynamic Selectors (Authority: Profile/Planning > Local)
    const [portion, setPortion] = useState('4');
    const [skillLevel, setSkillLevel] = useState(() => localStorage.getItem('smartSearch_skillLevel') || 'Acemi');

    // Recipe Modal State
    const [selectedRecipe, setSelectedRecipe] = useState(null);
    const [recipeModalOpen, setRecipeModalOpen] = useState(false);
    const [pendingRecipeForOpen, setPendingRecipeForOpen] = useState(null);
    const [showHealthPlusModal, setShowHealthPlusModal] = useState(false);
    const [showHealthPlusPaymentModal, setShowHealthPlusPaymentModal] = useState(false);
    const [healthPlusActive, setHealthPlusActive] = useState(false);
    const [healthPlusEntitled, setHealthPlusEntitled] = useState(false);
    const [healthPlusBusy, setHealthPlusBusy] = useState(false);
    const [forceOnboardingBabyModal, setForceOnboardingBabyModal] = useState(false);
    const [swapModalState, setSwapModalState] = useState({
        open: false,
        slotId: null,
        currentRecipe: null,
        alternatives: []
    });
    const [loadingElapsedSec, setLoadingElapsedSec] = useState(0);
    const {
        isCurrentMealCompleted,
        loading: menuCompletionLoading,
        refreshMenuCompletionState
    } = useMenuCompletionState({ mealSlot: currentMealSlot });
    const {
        showBabyModal,
        submitBabyData,
        closeBabyModal,
        loading: babyPromptLoading
    } = useBabyPrompt();

    const categories = [
        { id: 'soup', label: 'Çorba', icon: 'soup_kitchen' },
        { id: 'main', label: 'Ana Yemek', icon: 'restaurant' },
        { id: 'salad', label: 'Salata/Meze', icon: 'eco' },
        { id: 'dessert', label: 'Tatlı', icon: 'cake' }
    ];

    const blockedReasonLabels = {
        survey_incomplete: 'Anket tamamlanmadı',
        starter_inventory_missing: 'Başlangıç stoğu tamamlanmadı',
        smart_search_locked: 'Smart Search henüz aktif değil',
        notification_preference_missing: 'Bildirim tercihi eksik',
        push_permission_missing: 'Push izni verilmedi',
        operational_day_not_ready: 'Gün döngüsü henüz hazır değil',
        baby_context_incomplete: 'Bebek bilgileri eksik'
    };
    const warmupStatusLabels = {
        queued: 'Sıraya alındı',
        running: 'Hazırlanıyor',
        completed: 'Hazırlandı',
        coalesced: 'Birleşti',
        locked: 'Kilitli',
        no_eligible_jobs: 'Uygun iş yok',
        failed: 'Başarısız',
        disabled: 'Kapalı'
    };
    const mealIntentStatusLabels = {
        eligible: 'Hazır',
        waiting_window: 'Pencere bekleniyor',
        waiting_snapshot: 'Snapshot bekleniyor',
        gate_blocked: 'Gate bloklu',
        consumed: 'Tamamlandı',
        slot_disabled: 'Slot kapalı',
        unsupported_slot: 'Desteklenmiyor'
    };

    const isRecipeInSelectedCategories = (recipe, selected = []) => {
        if (!recipe) return false;

        // [V12.100] Authority: Backend provided menuType
        const menuType = String(recipe.menuType || '').toLowerCase();
        const catIdx = Number(recipe?.categoryIndex);
        const style = String(recipe?.serveStyle || '').toLocaleLowerCase('tr-TR');
        const name = String(recipe?.name || recipe?.title || '').toLocaleLowerCase('tr-TR');

        const hasSoup = selected.includes('soup');
        const hasMain = selected.includes('main');
        const hasSalad = selected.includes('salad');
        const hasDessert = selected.includes('dessert');

        // Check Soup
        const isSoup = hasSoup && (
            menuType === 'soup' ||
            catIdx === 0 ||
            style === 'corba' || style === 'çorba' || style === 'soup' ||
            name.includes('çorba') || name.includes('corba')
        );

        // Check Main
        const isMain = hasMain && (
            menuType === 'main' ||
            catIdx === 1 || Number.isNaN(catIdx) || catIdx === 5 ||
            style === 'yan_yemek' || style === 'ana_yemek' || style === 'ana yemek' ||
            style === 'hamur_isi' || style === 'hamur_işi' || style === 'pastry' || style === 'main'
        );

        // Check Salad
        const isSalad = hasSalad && (
            menuType === 'salad' ||
            style === 'salata' || style === 'salad' || style === 'meze' ||
            name.includes('salata')
        );

        // Check Dessert
        const isDessert = hasDessert && (
            menuType === 'dessert' ||
            catIdx === 3 ||
            style === 'tatli' || style === 'tatlı' || style === 'dessert' ||
            name.includes('tatlı') || name.includes('tatli')
        );

        return isSoup || isMain || isSalad || isDessert;
    };

    const loadingSteps = [
        'Malzemeler kontrol ediliyor...',
        'Son Kullanma Tarihlerine bakıyoruz...',
        'Bütçeye uygunluk denetleniyor...',
        'Tam bir yemek menüsü ayarlanıyor...',
        'Eşlikçi yemekler hazırlanıyor...'
    ];
    const activeLoadingStep = loadingSteps[Math.min(loadingSteps.length - 1, Math.floor(loadingElapsedSec / 1.2))];
    const warmupStatus = String(recentWarmup?.status || '').trim().toLowerCase();
    const warmupLabel = warmupStatusLabels[warmupStatus] || 'Bekleniyor';
    const mealIntentStatus = String(mealIntentPush?.status || '').trim().toLowerCase();
    const mealIntentLabel = mealIntentStatusLabels[mealIntentStatus] || 'Bekleniyor';
    const triggerInfoVisible = Boolean(mealSlotTrigger?.slot || recentWarmup?.status || mealIntentPush?.status);
    const triggerToneClass = warmupStatus === 'completed'
        ? 'border-emerald-200 bg-emerald-50/90 text-emerald-700 dark:border-emerald-800/60 dark:bg-emerald-900/20 dark:text-emerald-300'
        : warmupStatus === 'running'
            ? 'border-sky-200 bg-sky-50/90 text-sky-700 dark:border-sky-800/60 dark:bg-sky-900/20 dark:text-sky-300'
            : warmupStatus === 'queued'
                ? 'border-amber-200 bg-amber-50/90 text-amber-700 dark:border-amber-800/60 dark:bg-amber-900/20 dark:text-amber-300'
                : 'border-slate-200 bg-white/80 text-slate-600 dark:border-slate-700 dark:bg-slate-900/40 dark:text-slate-300';

    useEffect(() => {
        if (!loading) {
            setLoadingElapsedSec(0);
            return;
        }
        const startedAt = Date.now();
        const timer = window.setInterval(() => {
            const elapsed = Math.floor((Date.now() - startedAt) / 1000);
            setLoadingElapsedSec(elapsed);
        }, 250);
        return () => window.clearInterval(timer);
    }, [loading]);

    useEffect(() => {
        if (menuCompletionLoading) return;
        const initialize = async () => {
            setLoading(true);
            try {
                const token = localStorage.getItem('authToken');
                const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
                let data = null;
                for (let attempt = 0; attempt < 3; attempt += 1) {
                    const res = await fetch(`${API_BASE_URL}/api/smart-search/preload?mealSlot=${encodeURIComponent(currentMealSlot)}&intentSource=smart_search_preload`, {
                        headers: { 'Authorization': `Bearer ${token}` }
                    });
                    const payload = await res.json().catch(() => ({}));
                    if (!payload?.success) {
                        if (attempt === 2) throw new Error(payload?.error || 'preload_failed');
                        await wait(1200);
                        continue;
                    }
                    data = payload;
                    break;
                }
                if (!data?.success) throw new Error('preload_failed');

                const profile = data.profile || null;
                const configData = data.planningConfig || null;
                const defaults = data.defaults || {};
                const initialData = data.initialData || {};
                const initialState = String(initialData.meta?.state || data.meta?.state || '');

                setUserProfile(profile);
                setPlanningConfig(configData);
                setMealSlotActivation(data.mealSlotActivation || configData?.mealSlotActivation || null);
                setMealSlotTrigger(data.mealSlotTrigger || null);
                setRecentWarmup(data.recentWarmup || null);
                setMealIntentPush(data.mealIntentPush || null);

                const finalPortion = String(defaults.portion || profile?.servingSize || '4');
                const finalSkill = defaults.skillLevel || 'Acemi';
                const initialCategories = Array.isArray(defaults.selectedCategories) && defaults.selectedCategories.length > 0
                    ? defaults.selectedCategories
                    : ['main', 'soup', 'salad'];

                setPortion(finalPortion);
                setSkillLevel(finalSkill);
                localStorage.setItem('smartSearch_portion', finalPortion);
                localStorage.setItem('smartSearch_skillLevel', finalSkill);
                setSelectedCategories(initialCategories);

                setMatrix(initialData.matrix || null);
                setSavedValue(initialData.savedValue || initialData.matrix?.rescue?.reduce((sum, r) => sum + (r.savedValue || 0), 0) || 0);
                setAvailableComponents(initialData.availableComponents || []);
                setTotalCount(initialData.totalCount || initialData.recipes?.length || 0);
                setOnboardingGate(initialData.meta?.gate || data.meta?.gate || null);
                setGateState(initialState);
                updateSmartSearchNotice(initialData.meta || data.meta || null);
                setBlockedReasons(
                    Array.isArray(initialData.meta?.blockedReasons)
                        ? initialData.meta.blockedReasons
                        : Array.isArray(data.meta?.blockedReasons)
                            ? data.meta.blockedReasons
                            : []
                );

                if (initialData.structuredMenu) {
                    setStructuredMenu(initialData.structuredMenu);
                    setRecipes(initialData.recipes || []);
                } else {
                    setRecipes(initialData.recipes || []);
                    setStructuredMenu(null);
                    // If snapshot preload is empty, fall back to live recommendations.
                    const hasInitialRecipes = Array.isArray(initialData.recipes) && initialData.recipes.length > 0;
                    if (
                        !hasInitialRecipes &&
                        !isCurrentMealCompleted &&
                        initialState !== 'meal_slot_disabled' &&
                        initialState !== 'breakfast_separate_domain'
                    ) {
                        const recRes = await fetch(`${API_BASE_URL}/api/smart-search/recommendations`, {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${token}`
                            },
                            body: JSON.stringify({
                                preferredCategories: initialCategories,
                                servingSize: parseInt(finalPortion, 10) || 4,
                                skillLevel: finalSkill,
                                mealSlot: currentMealSlot,
                                intentSource: 'smart_search_preload_fallback',
                                page: 1,
                                pageSize: PAGE_SIZE
                            })
                        });
                        const recData = await recRes.json().catch(() => ({}));
                        if (recData?.success) {
                            updateSmartSearchNotice(recData.meta || null);
                            setStructuredMenu(recData.structuredMenu || null);
                            setRecipes(recData.recipes || []);
                            setMatrix(recData.matrix || null);
                            setMealSlotActivation(recData.mealSlotActivation || data.mealSlotActivation || configData?.mealSlotActivation || null);
                            setMealSlotTrigger(recData.mealSlotTrigger || data.mealSlotTrigger || null);
                            setRecentWarmup(recData.recentWarmup || null);
                            setMealIntentPush(recData.mealIntentPush || data.mealIntentPush || null);
                            setSavedValue(
                                recData.savedValue ||
                                recData.matrix?.rescue?.reduce((sum, r) => sum + (r.savedValue || 0), 0) ||
                                0
                            );
                            setAvailableComponents(recData.availableComponents || []);
                            setTotalCount(recData.totalCount || recData.recipes?.length || 0);
                            setOnboardingGate(recData.meta?.gate || null);
                            setGateState(String(recData.meta?.state || ''));
                            setBlockedReasons(Array.isArray(recData.meta?.blockedReasons) ? recData.meta.blockedReasons : []);
                        }
                    }
                }
            } catch (err) {
                console.error('Smart search preload error:', err);
                Swal.fire('Hata', 'Başlangıç verileri alınamadı', 'error');
            } finally {
                setLoading(false);
            }
        };

        initialize();
    }, [menuCompletionLoading, isCurrentMealCompleted, currentMealSlot]);

    const fetchHealthPlusState = async () => {
        try {
            const [profileRes, stateRes] = await Promise.all([
                fetch(`${API_BASE_URL}/api/profile`, {
                    credentials: 'include',
                    headers: getAuthHeaders()
                }),
                fetch(`${API_BASE_URL}/api/health/activation-state`, {
                    credentials: 'include',
                    headers: getAuthHeaders()
                })
            ]);
            const profileData = await profileRes.json().catch(() => ({}));
            const stateData = await stateRes.json().catch(() => ({}));
            const entitled = !!profileData?.entitlements?.modules?.global?.granted
                && !profileData?.entitlements?.lockedByTime;
            setHealthPlusEntitled(entitled);
            setHealthPlusActive(entitled && !!stateData?.enabled);
        } catch (error) {
            console.warn('health+ state fetch warning', error);
            setHealthPlusEntitled(false);
            setHealthPlusActive(false);
        }
    };

    useEffect(() => {
        fetchHealthPlusState();
    }, []);

    useEffect(() => {
        const loadOnboardingResolver = async () => {
            try {
                const token = localStorage.getItem('authToken');
                if (!token) return;
                const [stateRes, resolverRes] = await Promise.all([
                    fetch(`${API_BASE_URL}/api/profile/onboarding/day-state`, {
                        credentials: 'include',
                        headers: { Authorization: `Bearer ${token}` }
                    }),
                    fetch(`${API_BASE_URL}/api/profile/onboarding/day-resolver`, {
                        credentials: 'include',
                        headers: { Authorization: `Bearer ${token}` }
                    })
                ]);
                const stateData = await stateRes.json().catch(() => ({}));
                const resolverData = await resolverRes.json().catch(() => ({}));
                const dayState = stateData?.dayState || {};
                const resolver = resolverData?.resolver || null;
                const onboardingDay = Number(resolver?.onboardingDay || 0);
                const completedSteps = new Set(Array.isArray(dayState?.completedSteps) ? dayState.completedSteps : []);
                const isAnswered = completedSteps.has('baby_signal_answered');
                const optedOut = Boolean(dayState?.babyDetailOptOut);

                if (onboardingDay === 1 && !isAnswered && !optedOut) {
                    setForceOnboardingBabyModal(true);
                }
            } catch (_error) {
                // no-op
            }
        };
        loadOnboardingResolver();
    }, []);

    const setHealthPlusActivationState = async (enabled) => {
        try {
            setHealthPlusBusy(true);
            const res = await fetch(`${API_BASE_URL}/api/health/activation-state`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
                body: JSON.stringify({ enabled: !!enabled }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.error || 'health_activation_state_update_failed');
            setHealthPlusActive(!!data.enabled);
        } catch (error) {
            console.error('health+ toggle failed', error);
            Swal.fire('Hata', 'Sağlık+ durumu güncellenemedi.', 'error');
        } finally {
            setHealthPlusBusy(false);
        }
    };

    const handleHealthPlusCardClick = async () => {
        if (healthPlusBusy) return;
        if (healthPlusActive) {
            await setHealthPlusActivationState(false);
            return;
        }
        if (healthPlusEntitled) {
            await setHealthPlusActivationState(true);
            return;
        }
        setShowHealthPlusModal(true);
    };

    const handleHealthPlusProceedToPayment = () => {
        setShowHealthPlusModal(false);
        setShowHealthPlusPaymentModal(true);
    };

    const fetchPlanningConfig = async () => {
        try {
            const token = localStorage.getItem('authToken');
            const res = await fetch(`${API_BASE_URL}/api/planning/config`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setPlanningConfig(data);
                return data;
            }
        } catch (err) {
            console.error('Planning config fetch error:', err);
        }
        return null;
    };

    const fetchProfile = async () => {
        try {
            const token = localStorage.getItem('authToken');
            const res = await fetch(`${API_BASE_URL}/api/profile`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setUserProfile(data);
                return data;
            }
        } catch (err) {
            console.error('Profile fetch error:', err);
        }
    };

    const handleInventorySearch = async (cats, page = 1, forcedPortion = null) => {
        if (isCurrentMealCompleted || currentMealSlot === 'breakfast') {
            setLoading(false);
            return;
        }
        setLoading(true);
        setCurrentPage(page);
        try {
            const requestStartedAt = performance.now();
            const currentPortion = forcedPortion || parseInt(portion);
            const token = localStorage.getItem('authToken');
            const response = await fetch(`${API_BASE_URL}/api/smart-search/recommendations`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    preferredCategories: cats,
                    servingSize: currentPortion,
                    skillLevel: skillLevel,
                    mealSlot: currentMealSlot,
                    intentSource: 'smart_search_ui',
                    page,
                    pageSize: PAGE_SIZE
                })
            });
            const data = await response.json();
            const requestMs = performance.now() - requestStartedAt;
            console.log(
                `[PERF][UI SmartSearch Recommendations] page=${page} portion=${currentPortion} took=${requestMs.toFixed(1)}ms route=${response.headers.get('X-BBTV-Route-Total-Ms') || '-'}ms orchestrator=${response.headers.get('X-BBTV-Orchestrator-Ms') || '-'}ms returned=${data?.recipes?.length || 0}`
            );
            if (data.success) {
                setOnboardingGate(data.meta?.gate || null);
                setGateState(String(data.meta?.state || ''));
                setBlockedReasons(Array.isArray(data.meta?.blockedReasons) ? data.meta.blockedReasons : []);
                setMealSlotActivation(data.mealSlotActivation || null);
                setMealSlotTrigger(data.mealSlotTrigger || null);
                setRecentWarmup(data.recentWarmup || null);
                setMealIntentPush(data.mealIntentPush || null);
                updateSmartSearchNotice(data.meta || null);
                // [V12.28 PERF] Heavy JSON log removed to prevent UI/Terminal lag
                console.log(`[SmartSearch API] Success: ${data.recipes?.length || 0} recipes found.`);

                // [V12.30 Kitchen OS] Update Matrix & Savings states
                setMatrix(data.matrix || null);
                setSavedValue(data.savedValue || data.matrix?.rescue?.reduce((sum, r) => sum + (r.savedValue || 0), 0) || 0);

                if (data.matrix) {
                    // Map compact matrix records to standard recipe objects for UI compatibility
                    const mapM = (m, matrixTypeRaw) => {
                        const finalMatch = m.matchPercentage ?? m.match ?? 0;
                        const matrixType = String(matrixTypeRaw || 'unknown');
                        const selectedPortion = parseInt(portion) || 1;
                        console.log(`[Matrix Trace] ${matrixType}: ${m.name} -> ${finalMatch}% (ID: ${m.id})`);
                        return {
                            id: m.id,
                            name: m.name,
                            title: m.name,
                            imageUrl: m.image || m.imageUrl,
                            score: m.score,
                            isMatrix: true,
                            matrixType,
                            match: {
                                isFull: Number(m.missing || 0) === 0 && Number(finalMatch) >= 100,
                                isCookable: Number(m.missing || 0) === 0,
                                matchPercentage: finalMatch,
                                missingCount: Number(m.missing || 0),
                                missingIngredients: new Array(m.missing || 0).fill({})
                            },
                            calories: m.calories,
                            cookTime: m.cookTime,
                            servings: m.servings,
                            totalCost: m.totalCost,
                            priceSnapshot: {
                                total: Number(m.totalCost || 0),
                                breakdown: [],
                                currency: 'TRY',
                                portionBasis: Number(selectedPortion || m.servings || 1),
                                isScaled: true,
                                unknownItems: 0,
                                source: 'MATRIX'
                            },
                            fixedTime: m.fixedTime,
                            variableTime: m.variableTime,
                            scalingModel: m.scalingModel,
                            isPreScaled: true, // [V12.94] Matrix is ALWAYS pre-scaled from backend
                            presentation: {
                                isPreScaled: true,
                                appliedPortion: selectedPortion
                            },
                            targetPortion: selectedPortion
                        };
                    };

                    // [V12.99 CRITICAL FIX] The Orchestrator ALREADY beautifully mapped and enriched everything into `data.recipes`
                    // Do NOT manually reconstruct via mapM from compact matrix data!
                    // If data.recipes exists and has the matrix recipes, USE IT!
                    if (data.recipes && data.recipes.length > 0) {
                        setRecipes(data.recipes);
                    } else {
                        // Fallback ONLY
                        const matrixRecipes = [
                            ...(data.matrix.rescue || []).map(m => mapM(m, 'rescue')),
                            ...(data.matrix.breakfast || []).map(m => mapM(m, 'breakfast')),
                            ...(data.matrix.dinner || []).map(m => mapM(m, 'dinner'))
                        ];
                        setRecipes(matrixRecipes);
                    }

                    // [V12.40] If backend already structured it, use it!
                    if (data.structuredMenu) {
                        setStructuredMenu(data.structuredMenu);
                    }
                } else if (data.structuredMenu) {
                    setStructuredMenu(data.structuredMenu);
                    setRecipes(data.recipes || []); // Keep grid full for Double Visibility
                } else {
                    setRecipes(data.recipes || []);
                    setStructuredMenu(null);
                }

                setAvailableComponents(data.availableComponents || []);
                setTotalCount(data.totalCount || data.recipes?.length || (data.matrix ? (data.matrix.rescue?.length || 0) + (data.matrix.breakfast?.length || 0) + (data.matrix.dinner?.length || 0) : 0));

                if (data.meta?.hit) {
                    console.log(`[Kitchen OS] Matrix Hit! Response time: ${data.meta.duration}ms`);
                }
            } else {
                Swal.fire('Hata', data.message || 'Öneri alınamadı', 'error');
            }
        } catch (err) {
            console.error('Smart search error:', err);
            Swal.fire('Hata', 'Sunucu bağlantı hatası', 'error');
        } finally {
            setLoading(false);
        }
    };

    const toggleCategory = (catId) => {
        const newCats = viewMode === 'browse_all'
            ? [catId]
            : (selectedCategories.includes(catId)
                ? selectedCategories.filter(c => c !== catId)
                : [...selectedCategories, catId]);
        setSelectedCategories(newCats);
        setPreferencesChanged(true);
        setCurrentPage(1); // Reset page on category change

        // Improvement: In Browse All mode, refresh immediately
        if (viewMode === 'browse_all') {
            handleBrowseAll(newCats, 1);
        }
    };

    const toggleSelection = (recipeOrRecipes) => {
        const recipesToToggle = Array.isArray(recipeOrRecipes) ? recipeOrRecipes : [recipeOrRecipes];
        const namesToToggle = recipesToToggle.map(r => r.name || r.title);

        let newSelection = [...selectedRecipeNames];
        const allSelected = namesToToggle.every(name => newSelection.includes(name));

        if (allSelected) {
            // Remove all
            newSelection = newSelection.filter(name => !namesToToggle.includes(name));
        } else {
            // Add those not yet selected
            namesToToggle.forEach(name => {
                if (!newSelection.includes(name)) newSelection.push(name);
            });
        }
        setSelectedRecipeNames(newSelection);
    };

    const handleRecipeClick = async (recipe) => {
        if (!babyPromptLoading && showBabyModal) {
            setPendingRecipeForOpen(recipe);
            return;
        }

        let liveProfile = userProfile;
        try {
            const latest = await fetchProfile();
            if (latest) liveProfile = latest;
        } catch (_error) {
            // no-op
        }

        const selectedPortion = parseInt(portion) || 1;
        const hydrated = await hydrateRecipeForModal(recipe, {
            targetPortion: selectedPortion
        });
        const hasBabySignal = Boolean(
            liveProfile?.hasBaby ||
            liveProfile?.bebek_var_mi ||
            liveProfile?.babyAgeGroup ||
            liveProfile?.bebek_yas_grubu ||
            liveProfile?.intakeSurvey?.answers?.q7 === 'Evet' ||
            Array.isArray(liveProfile?.onboardingStates?.dayState?.completedSteps) &&
                liveProfile.onboardingStates.dayState.completedSteps.includes('baby_signal_answered')
        );
        setSelectedRecipe({
            ...hydrated,
            _liveUserProfile: liveProfile || null,
            _hasBabySignal: hasBabySignal,
            _babyNameFallback: liveProfile?.babyName || 'bebeğiniz'
        });
        setRecipeModalOpen(true);
    };

    const handleBabyModalSubmit = async (babyData) => {
        await submitBabyData(babyData);
        setForceOnboardingBabyModal(false);
        try {
            const token = localStorage.getItem('authToken');
            if (token) {
                const stateRes = await fetch(`${API_BASE_URL}/api/profile/onboarding/day-state`, {
                    credentials: 'include',
                    headers: { Authorization: `Bearer ${token}` }
                });
                const stateJson = await stateRes.json().catch(() => ({}));
                const currentSteps = Array.isArray(stateJson?.dayState?.completedSteps)
                    ? stateJson.dayState.completedSteps
                    : [];
                const mergedSteps = Array.from(new Set([...currentSteps, 'baby_signal_answered']));
                await fetch(`${API_BASE_URL}/api/profile/onboarding/day-state`, {
                    method: 'POST',
                    credentials: 'include',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`
                    },
                    body: JSON.stringify({
                        completedSteps: mergedSteps
                    })
                });
            }
        } catch (_error) {
            // no-op
        }
        if (!pendingRecipeForOpen) return;

        let liveProfile = userProfile;
        try {
            const latest = await fetchProfile();
            if (latest) liveProfile = latest;
        } catch (_error) {
            // no-op
        }

        const selectedPortion = parseInt(portion) || 1;
        const hydrated = await hydrateRecipeForModal(pendingRecipeForOpen, {
            targetPortion: selectedPortion
        });
        const hasBabySignal = Boolean(
            liveProfile?.hasBaby ||
            liveProfile?.bebek_var_mi ||
            liveProfile?.babyAgeGroup ||
            liveProfile?.bebek_yas_grubu ||
            liveProfile?.intakeSurvey?.answers?.q7 === 'Evet' ||
            Array.isArray(liveProfile?.onboardingStates?.dayState?.completedSteps) &&
                liveProfile.onboardingStates.dayState.completedSteps.includes('baby_signal_answered')
        );
        setSelectedRecipe({
            ...hydrated,
            _liveUserProfile: liveProfile || null,
            _hasBabySignal: hasBabySignal,
            _babyNameFallback: liveProfile?.babyName || 'bebeğiniz'
        });
        setRecipeModalOpen(true);
        setPendingRecipeForOpen(null);
    };

    const handleBabyModalClose = async () => {
        await closeBabyModal();
        setForceOnboardingBabyModal(false);
        setPendingRecipeForOpen(null);
    };

    const handleBrowseAll = async (cats, page = 1) => {
        // Prevent React Event objects from being passed as categories
        const finalCats = Array.isArray(cats) ? cats : selectedCategories;

        setBrowseLoading(true);
        setViewMode('browse_all');
        setCurrentPage(page);
        try {
            const requestStartedAt = performance.now();
            const token = localStorage.getItem('authToken');
            const response = await fetch(`${API_BASE_URL}/api/smart-search/browse-all`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    categories: finalCats,
                    servingSize: parseInt(portion),
                    page,
                    pageSize: PAGE_SIZE
                })
            });
            const data = await response.json();
            const requestMs = performance.now() - requestStartedAt;
            console.log(
                `[PERF][UI SmartSearch Browse] page=${page} pageSize=${PAGE_SIZE} took=${requestMs.toFixed(1)}ms route=${response.headers.get('X-BBTV-Route-Total-Ms') || '-'}ms orchestrator=${response.headers.get('X-BBTV-Orchestrator-Ms') || '-'}ms returned=${data?.recipes?.length || 0}`
            );
            if (data.success) {
                const rawRecipes = Array.isArray(data.recipes) ? data.recipes : [];
                const strictlyFiltered = rawRecipes.filter((recipe) => isRecipeInSelectedCategories(recipe, finalCats));
                const finalRecipes = strictlyFiltered.length > 0 || rawRecipes.length === 0
                    ? strictlyFiltered
                    : rawRecipes;
                setStructuredMenu(null);
                setRecipes([]);
                setBrowseRecipes(finalRecipes);
                setTotalCount(Number(data.totalCount) || finalRecipes.length);
            } else {
                Swal.fire('Hata', data.message || 'Tarifler yüklenemedi', 'error');
            }
        } catch (error) {
            console.error('Browse all error:', error);
            Swal.fire('Hata', 'Tarifler yüklenirken bir sorun oluştu', 'error');
        } finally {
            setBrowseLoading(false);
        }
    };

    const handleMarkMenuAsUsed = async () => {
        let recipesToProcess = [];
        if (viewMode === 'browse_all') {
            recipesToProcess = browseRecipes.filter(r => selectedRecipeNames.includes(r.name || r.title));
        } else if (structuredMenu) {
            // Collect from Structured Menu
            const { main, pair, soup, salad, dessert } = structuredMenu;
            const allStructured = [
                ...(main ? [main] : []),
                ...(pair || []),
                ...(soup ? [soup] : []),
                ...(salad ? [salad] : []),
                ...(dessert ? [dessert] : [])
            ];
            recipesToProcess = allStructured.filter(r => selectedRecipeNames.includes(r.name || r.title));
        } else {
            recipesToProcess = recipes.filter(r => selectedRecipeNames.includes(r.name || r.title));
        }

        if (recipesToProcess.length === 0) {
            Swal.fire({
                title: 'Tarif Seçmediniz',
                text: 'Lütfen pişirmek istediğiniz tariflerin üzerine tıklayarak seçin.',
                icon: 'info',
                confirmButtonText: 'Anladım'
            });
            return;
        }

        approveMenu(recipesToProcess, {
            sourceSurface: 'smart_search',
            mealSlot: currentMealSlot,
            currentServingSize: Number(portion || 4) || 4
        });
    };

    const handleAddMissingToShoppingList = async (missingNames = []) => {
        const recipe = selectedRecipe || {};
        const recipeName = recipe?.name || recipe?.title || 'Tarif';
        const normalizedRequested = new Set(
            (Array.isArray(missingNames) ? missingNames : [])
                .map((name) => normalizeRecipeIngredientName(name))
                .filter(Boolean)
        );
        const recipeMissing = getRecipeMissingIngredients(recipe);
        const matchedMissing = normalizedRequested.size > 0
            ? recipeMissing.filter((entry) => normalizedRequested.has(normalizeRecipeIngredientName(entry?.name)))
            : recipeMissing;
        const fallbackMissing = matchedMissing.length > 0
            ? matchedMissing
            : (Array.isArray(missingNames) ? missingNames : [])
                .map((name) => ({ name, amount: 1, unit: 'adet' }))
                .filter((entry) => String(entry.name || '').trim());
        const items = buildMissingShoppingItems(recipe, fallbackMissing);

        if (items.length === 0) {
            Swal.fire('Bilgi', 'Listeye eklenecek eksik malzeme bulunamadı.', 'info');
            return;
        }

        try {
            const token = localStorage.getItem('authToken') || localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/api/shopping/lists`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...getAuthHeaders(token ? { Authorization: `Bearer ${token}` } : {})
                },
                body: JSON.stringify({
                    name: `${recipeName} - Eksikler`,
                    items
                })
            });
            const payload = await response.json().catch(() => ({}));
            if (!response.ok) {
                throw new Error(payload?.message || payload?.error || 'Liste kaydedilemedi.');
            }

            window.dispatchEvent(new Event('Alakart_SYNC'));
            window.dispatchEvent(new Event('shoppingListUpdated'));
            Swal.fire({
                title: 'Sepete Eklendi',
                text: `${items.length} eksik malzeme alışveriş listene eklendi.`,
                icon: 'success',
                timer: 1800,
                showConfirmButton: false
            });
        } catch (error) {
            console.error('Smart Search missing shopping list error:', error);
            Swal.fire('Hata', error.message || 'Eksikler listeye eklenemedi.', 'error');
        }
    };

    const getCurrentMenuRecipes = () => {
        if (viewMode === 'browse_all') {
            return Array.isArray(browseRecipes) ? browseRecipes : [];
        }
        if (structuredMenu) {
            const { main, pair, soup, salad, dessert } = structuredMenu;
            return [
                ...(main ? [main] : []),
                ...(pair || []),
                ...(soup ? [soup] : []),
                ...(salad ? [salad] : []),
                ...(dessert ? [dessert] : [])
            ].filter(Boolean);
        }
        return Array.isArray(recipes) ? recipes : [];
    };

    const handlePrimaryMenuAction = () => {
        if (isCurrentMealCompleted || isApproving) return;
        if (selectedRecipeNames.length === 0) {
            const menuRecipes = getCurrentMenuRecipes();
            if (menuRecipes.length === 0) return;
            const allNames = Array.from(new Set(menuRecipes.map((r) => r?.name || r?.title).filter(Boolean)));
            setSelectedRecipeNames(allNames);
            return;
        }
        handleMarkMenuAsUsed();
    };

    const handleSwapStructuredSlot = async (slotId, currentRecipe) => {
        try {
            const token = localStorage.getItem('authToken');
            const response = await fetch(`${API_BASE_URL}/api/smart-search/swap-slot-v2`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    slot: slotId,
                    currentRecipeId: currentRecipe?.id,
                    preferredCategories: selectedCategories,
                    servingSize: parseInt(portion, 10) || 4,
                    skillLevel
                })
            });
            const data = await response.json().catch(() => ({}));
            if (!response.ok || !data?.success || !data?.replacement) {
                Swal.fire('Bilgi', data?.message || 'Uygun alternatif bulunamadı.', 'info');
                return;
            }

            const alternatives = Array.isArray(data?.alternatives) && data.alternatives.length > 0
                ? data.alternatives
                : [data.replacement].filter(Boolean);
            setSwapModalState({
                open: true,
                slotId,
                currentRecipe,
                alternatives
            });
        } catch (error) {
            console.error('SmartSearch swap slot error:', error);
            Swal.fire('Hata', 'Alternatif yüklenirken bir sorun oluştu.', 'error');
        }
    };

    const applyStructuredSlotReplacement = (slotId, currentRecipe, replacement) => {
        if (!replacement) return;

        setStructuredMenu((prev) => {
            if (!prev || typeof prev !== 'object') return prev;
            if (slotId === 'main') {
                const nextPair = Array.isArray(replacement?.suggestedPairings) && replacement.suggestedPairings.length > 0
                    ? replacement.suggestedPairings
                    : (Array.isArray(prev.pair) ? prev.pair : []);
                return {
                    ...prev,
                    main: { ...replacement, suggestedPairings: nextPair },
                    pair: nextPair
                };
            }
            return { ...prev, [slotId]: replacement };
        });

        const replaceRecipeInList = (list = []) => {
            if (!Array.isArray(list) || list.length === 0) return list;
            const currentRecipeId = currentRecipe?.id == null ? null : String(currentRecipe.id);
            let replaced = false;
            const nextList = list.map((recipe) => {
                const recipeId = recipe?.id == null ? null : String(recipe.id);
                const sameRecipe = currentRecipeId && recipeId === currentRecipeId;
                const sameSlot = matchesRecipeToSlot(recipe, slotId);
                if (!replaced && (sameRecipe || sameSlot)) {
                    replaced = true;
                    return replacement;
                }
                return recipe;
            });
            return nextList;
        };

        setRecipes((prev) => replaceRecipeInList(prev));
        setBrowseRecipes((prev) => replaceRecipeInList(prev));

        setSwapModalState({
            open: false,
            slotId: null,
            currentRecipe: null,
            alternatives: []
        });

        Swal.fire({
            toast: true,
            position: 'top-end',
            icon: 'success',
            title: 'Alternatif seçimin menüye işlendi',
            showConfirmButton: false,
            timer: 1400
        });
    };

    const matchesRecipeToSlot = (recipe, slotId = 'main') => {
        const normalizedSlot = String(slotId || '').toLowerCase();
        const menuType = String(recipe?.menuType || '').toLowerCase();
        const style = String(recipe?.serveStyle || '').toLocaleLowerCase('tr-TR');
        const catIdx = Number(recipe?.categoryIndex);

        if (normalizedSlot === 'soup') {
            return menuType === 'soup' || catIdx === 0 || style === 'corba' || style === 'çorba' || style === 'soup';
        }
        if (normalizedSlot === 'salad') {
            return menuType === 'salad' || catIdx === 2 || style === 'salata' || style === 'salad' || style === 'meze';
        }
        if (normalizedSlot === 'dessert') {
            return menuType === 'dessert' || catIdx === 3 || style === 'tatli' || style === 'tatlı' || style === 'dessert';
        }

        return (
            menuType === 'main' ||
            catIdx === 1 ||
            Number.isNaN(catIdx) ||
            catIdx === 5 ||
            style === 'yan_yemek' ||
            style === 'ana_yemek' ||
            style === 'ana yemek' ||
            style === 'hamur_isi' ||
            style === 'hamur_işi' ||
            style === 'pastry' ||
            style === 'main'
        );
    };

    const totalPages = Math.ceil(totalCount / PAGE_SIZE);

    const renderPagination = (position = 'bottom') => {
        if (totalPages <= 1) return null;

        const handlePageChange = (newPage) => {
            if (newPage < 1 || newPage > totalPages) return;
            if (viewMode === 'browse_all') {
                handleBrowseAll(selectedCategories, newPage);
            } else {
                handleInventorySearch(selectedCategories, newPage);
            }
            // Scroll to results top on change
            const resultsSection = document.getElementById('search-results-start');
            if (resultsSection) resultsSection.scrollIntoView({ behavior: 'smooth' });
        };

        const pages = [];
        const maxVisiblePages = 5;
        let startPage = Math.max(1, currentPage - 2);
        let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

        if (endPage - startPage < maxVisiblePages - 1) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }

        for (let i = startPage; i <= endPage; i++) {
            pages.push(
                <button
                    key={i}
                    onClick={() => handlePageChange(i)}
                    className={`min-w-[40px] h-10 flex items-center justify-center rounded-xl font-black text-sm transition-all ${currentPage === i
                        ? 'bg-red-500 text-white shadow-lg shadow-red-500/30 scale-110'
                        : 'bg-white dark:bg-slate-800 text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 border border-slate-200 dark:border-slate-700 shadow-sm'
                        }`}
                >
                    {i}
                </button>
            );
        }

        return (
            <div className={`flex flex-col sm:flex-row items-center justify-center gap-4 ${position === 'top' ? 'mb-8' : 'mt-12'}`}>
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="px-4 h-10 flex items-center gap-2 rounded-xl font-black text-xs bg-white dark:bg-slate-800 text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 border border-slate-200 dark:border-slate-700 disabled:opacity-30 disabled:grayscale transition-all shadow-sm"
                    >
                        <span className="material-symbols-outlined text-[18px]">chevron_left</span>
                        Önceki
                    </button>
                    <div className="flex items-center gap-1.5 mx-2">
                        {pages}
                    </div>
                    <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="px-4 h-10 flex items-center gap-2 rounded-xl font-black text-xs bg-white dark:bg-slate-800 text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 border border-slate-200 dark:border-slate-700 disabled:opacity-30 disabled:grayscale transition-all shadow-sm"
                    >
                        Sonraki
                        <span className="material-symbols-outlined text-[18px]">chevron_right</span>
                    </button>
                </div>
                <div className="flex items-center gap-3 px-4 py-2 bg-slate-100/50 dark:bg-slate-800/50 rounded-full border border-slate-200/50 dark:border-slate-700/50">
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-tighter">SAYFA</span>
                    <span className="text-sm font-black text-red-500">{currentPage}</span>
                    <span className="text-[10px] font-black text-slate-300">/</span>
                    <span className="text-sm font-black text-slate-500 dark:text-slate-400">{totalPages}</span>
                </div>
            </div>
        );
    };

    return (
        <DashboardLayout isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode}>
            <div className="flex flex-col h-full bg-white dark:bg-[#1a1a1a] transition-colors duration-200">
                {/* Header Section */}
                <div className="px-6 py-6 border-b border-slate-100 dark:border-slate-800">
                    <div className="max-w-7xl mx-auto">
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                            <div>
                                <h1 className="text-3xl font-black bg-gradient-to-r from-rose-500 to-rose-600 bg-clip-text text-transparent">Akıllı Mutfak</h1>
                                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 font-medium italic">Mutfağındaki son kullanma tarihi yaklaşan ürünlere göre ailenize özel üretilen menüler.</p>
                            </div>
                            <div className="flex flex-col sm:flex-row items-center gap-4">
                                <SavedValueWidget value={savedValue} isDarkMode={isDarkMode} />
                            </div>
                        </div>

                        <HealthPlusMenuCard
                            isActive={healthPlusActive}
                            inactiveButtonLabel={healthPlusEntitled ? 'Sağlık+ başlat' : 'Sağlık+ aktivasyonu'}
                            onClick={handleHealthPlusCardClick}
                            className="mt-6"
                        />

                        {smartSearchNotice && (
                            <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-amber-900 shadow-sm dark:border-amber-700/60 dark:bg-amber-950/40 dark:text-amber-100">
                                <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                                    <div className="flex items-start gap-3">
                                        <span className="material-symbols-outlined mt-0.5 text-[20px]">info</span>
                                        <p className="text-sm font-bold leading-relaxed">{smartSearchNotice.message}</p>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => navigate('/my-shopping-lists')}
                                        className="inline-flex h-10 shrink-0 items-center justify-center gap-2 rounded-xl bg-amber-600 px-4 text-xs font-black text-white shadow-sm transition hover:bg-amber-700 active:scale-95"
                                    >
                                        <span className="material-symbols-outlined text-[18px]">shopping_cart</span>
                                        Alışverişe git
                                    </button>
                                </div>
                            </div>
                        )}


                     {/*  <HealthMedicalNotice
                            enabled={healthPlusActive}
                            targetMemberKey="household"
                            className="mt-4"
                        />
                        {triggerInfoVisible && (
                            <div className={`mt-4 rounded-2xl border px-4 py-3 ${triggerToneClass}`}>
                                <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                                    <div className="flex items-center gap-2">
                                        <span className="material-symbols-outlined text-[18px]">schedule</span>
                                        <p className="text-xs font-black uppercase tracking-[0.2em]">Öğün Trigger Durumu</p>
                                    </div>
                                    <div className="text-sm font-bold">
                                        {currentMealLabel} • Warmup: {warmupLabel} • Push: {mealIntentLabel}
                                    </div>
                                </div>
                                <div className="mt-2 flex flex-wrap gap-2 text-xs font-semibold">
                                    {mealSlotTrigger?.scheduledTime && (
                                        <span className="rounded-full bg-black/5 px-3 py-1 dark:bg-white/10">
                                            Slot saati: {mealSlotTrigger.scheduledTime}
                                        </span>
                                    )}
                                    {mealSlotTrigger?.preTriggerTime && (
                                        <span className="rounded-full bg-black/5 px-3 py-1 dark:bg-white/10">
                                            Pre-trigger: {mealSlotTrigger.preTriggerTime}
                                        </span>
                                    )}
                                    {mealSlotTrigger?.phase && (
                                        <span className="rounded-full bg-black/5 px-3 py-1 dark:bg-white/10">
                                            Faz: {mealSlotTrigger.phase}
                                        </span>
                                    )}
                                    {mealIntentPush?.status && (
                                        <span className="rounded-full bg-black/5 px-3 py-1 dark:bg-white/10">
                                            Push intent: {mealIntentLabel}
                                        </span>
                                    )}
                                    {mealIntentPush?.reason && (
                                        <span className="rounded-full bg-black/5 px-3 py-1 dark:bg-white/10">
                                            Neden: {mealIntentPush.reason}
                                        </span>
                                    )}
                                    {recentWarmup?.updatedAt && (
                                        <span className="rounded-full bg-black/5 px-3 py-1 dark:bg-white/10">
                                            Son güncelleme: {new Date(recentWarmup.updatedAt).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                                        </span>
                                    )}
                                </div>
                            </div>
                        )}  */}
                        {/* Responsive Filter Toolbar */}
                        <div className="flex flex-col-reverse lg:flex-row lg:items-end gap-6 mt-8 p-5 md:p-6 bg-slate-50/50 dark:bg-slate-900/30 rounded-[2rem] border border-slate-100 dark:border-slate-800/50">

                            {/* Left/Bottom: Categories (Filters) */}
                            <div className="flex-1">
                                <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 pl-1 mb-3">MENÜDE NELER OLSUN?</p>
                                <div className="flex flex-wrap gap-2">
                                    {categories.map(cat => {
                                        const isActive = selectedCategories.includes(cat.id);
                                        const isBrowse = viewMode === 'browse_all';

                                        const activeColors = isBrowse ? {
                                            soup: 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 border-amber-200 dark:border-amber-800/50',
                                            main: 'bg-rose-100 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400 border-rose-200 dark:border-rose-800/50',
                                            salad: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800/50',
                                            dessert: 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 border-purple-200 dark:border-purple-800/50'
                                        } : {
                                            soup: 'bg-amber-500 text-white border-amber-600',
                                            main: 'bg-rose-500 text-white border-rose-600',
                                            salad: 'bg-emerald-500 text-white border-emerald-600',
                                            dessert: 'bg-purple-500 text-white border-purple-600'
                                        };

                                        return (
                                            <button
                                                key={cat.id}
                                                onClick={() => toggleCategory(cat.id)}
                                                className={`flex items-center justify-center gap-2 px-4 py-3 rounded-2xl border-2 transition-all active:scale-95 ${isActive
                                                    ? `${activeColors[cat.id]} shadow-md`
                                                    : 'bg-white dark:bg-slate-900 border-slate-100 dark:border-800 text-slate-500 hover:border-slate-300 dark:hover:border-slate-700'
                                                    }`}
                                            >
                                                <span className="material-symbols-outlined text-[20px]">{cat.icon}</span>
                                                <span className="text-xs font-bold">{cat.label}</span>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Responsive Divider (Vertical on Desktop, Horizontal/Hidden on Mobile) */}
                            <div className="hidden lg:block w-px h-12 bg-slate-200 dark:bg-slate-800 self-center opacity-50" />

                            {/* Right/Top: Preferences (Portion & Skill) */}
                            <div className="flex flex-col sm:flex-row lg:items-center gap-4">
                                {/* Portion Selector */}
                                <div className="flex flex-col gap-2">
                                    <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 pl-1">KİŞİ</p>
                                    <div className="relative h-12 w-full lg:w-32">
                                        <input
                                            type="number"
                                            min={1}
                                            value={portion}
                                            onChange={e => {
                                                const val = e.target.value;
                                                setPortion(val);
                                                localStorage.setItem('smartSearch_portion', val);
                                                // [REACTIVE FIX] Auto-fetch on portion change using the FRESH value (val)
                                                handleInventorySearch(selectedCategories, 1, parseInt(val));
                                                setPreferencesChanged(false);
                                            }}
                                            className="w-full h-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl pl-5 pr-10 text-sm font-black focus:border-rose-500 transition-all outline-none shadow-sm"
                                        />
                                        <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[10px] text-slate-400 font-bold pointer-events-none italic">Kişi</span>
                                    </div>
                                </div>

                                {/* Skill Selector */}
                                <div className="flex flex-col gap-2">
                                    <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 pl-1">TECRÜBE</p>
                                    <div className="flex bg-white dark:bg-slate-900 p-1 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm h-12">
                                        {['Acemi', 'Deneyimli', 'Usta Şef'].map((lvl) => (
                                            <button
                                                key={lvl}
                                                onClick={() => {
                                                    setSkillLevel(lvl);
                                                    localStorage.setItem('smartSearch_skillLevel', lvl);
                                                    setPreferencesChanged(true);
                                                }}
                                                className={`flex-1 flex items-center justify-center px-4 rounded-xl text-[10px] font-black transition-all ${skillLevel === lvl
                                                    ? 'bg-rose-500 text-white shadow-md'
                                                    : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-200'
                                                    }`}
                                            >
                                                {lvl.includes('Usta') ? 'Usta' : lvl}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* Action Button */}
                                <div className="flex flex-col gap-2">
                                    <p className="hidden lg:block text-[10px] font-black uppercase tracking-widest text-transparent pl-1 mb-0.5">İŞLEM</p>
                                    <button
                                        onClick={() => {
                                            handleInventorySearch(selectedCategories);
                                            setPreferencesChanged(false);
                                        }}
                                        className={`h-12 px-6 rounded-2xl font-black text-xs transition-all flex items-center gap-2 shadow-lg active:scale-95 ${preferencesChanged || recipes.length === 0
                                            ? 'bg-gradient-to-r from-rose-600 to-rose-700 text-white hover:shadow-rose-500/30'
                                            : 'bg-white dark:bg-slate-800 text-slate-400 border border-slate-200 dark:border-slate-800 shadow-none'
                                            }`}
                                    >
                                        <span className="material-symbols-outlined text-[18px]">magic_button</span>
                                        Menüyü Hazırla
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Main Content Area */}
                <div className="flex-1 overflow-y-auto p-6 pb-32 no-scrollbar">
                    <div className="max-w-7xl mx-auto">
                        {!loading && isCurrentMealCompleted && (
                            <div className="mb-6">
                                <MenuCompletionBanner
                                    isDarkMode={isDarkMode}
                                    className="max-w-3xl mx-auto"
                                    subtitle={`${currentMealLabel} öğününü tamamladık.`}
                                    detail="Diğer öğünler için yeniden öneri alabilirsin."
                                />
                            </div>
                        )}
                        {viewMode === 'browse_all' ? (
                            <div className="animate-in fade-in slide-in-from-bottom-4 duration-300" id="search-results-start">
                                <div className="mb-8 flex flex-col sm:flex-row items-center justify-between gap-4 sticky top-0 bg-white/90 dark:bg-[#1a1a1a]/90 backdrop-blur-xl py-5 z-20 border-b border-slate-100 dark:border-slate-800 -mx-6 px-6">
                                    <button
                                        onClick={() => setViewMode('smart_menu')}
                                        className="flex items-center gap-2 px-5 py-2.5 bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300 font-black text-xs hover:border-rose-500 hover:text-rose-500 transition-all shadow-sm active:scale-95 shrink-0"
                                    >
                                        <span className="material-symbols-outlined text-[20px]">arrow_back</span>
                                        Akıllı Menüye Dön
                                    </button>

                                    <div className="flex flex-col items-center sm:items-end text-center sm:text-right">
                                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-0.5">VERİTABANI TARANIYOR</p>
                                        <div className="flex items-center gap-2">
                                            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                                            <p className="text-lg font-black text-slate-800 dark:text-white leading-none">
                                                {totalCount} <span className="text-slate-400 text-xs font-bold uppercase ml-1">TARİF BULUNDU</span>
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {renderPagination('top')}

                                {browseLoading ? (
                                    <div className="flex flex-col items-center justify-center h-80 space-y-4">
                                        <div className="w-12 h-12 border-4 border-rose-500 border-t-transparent rounded-full animate-spin" />
                                        <p className="font-bold animate-pulse text-rose-500">Tüm tarifler getiriliyor...</p>
                                    </div>
                                ) : (
                                    <div className={`grid grid-cols-1 md:grid-cols-2 ${browseRecipes.some((recipe) => Array.isArray(recipe?.suggestedPairings) && recipe.suggestedPairings.length > 0) ? 'lg:grid-cols-4' : 'lg:grid-cols-3'} gap-6`}>
                                        {composeBrowseRows(browseRecipes).map((recipe, idx) => {
                                            const hasPairing = Array.isArray(recipe?.suggestedPairings) && recipe.suggestedPairings.length > 0;

                                            return (
                                                <div
                                                    key={recipe.id || `${recipe.name || recipe.title}-${idx}`}
                                                    className={hasPairing ? 'lg:col-span-2' : 'lg:col-span-1'}
                                                >
                                                    <RecipeMenuGroup
                                                        recipe={recipe}
                                                        isDarkMode={isDarkMode}
                                                        onCardClick={handleRecipeClick}
                                                        onToggleSelect={toggleSelection}
                                                        selectedRecipeNames={selectedRecipeNames}
                                                        isCompleted={isCurrentMealCompleted}
                                                        isModern={true}
                                                        layoutMode={hasPairing ? 'browseFeaturedPair' : 'compact'}
                                                        userProfile={userProfile}
                                                        userPortion={parseInt(portion)}
                                                    />
                                                </div>
                                            )
                                        })}
                                    </div>
                                )}

                                {renderPagination()}
                            </div>
                        ) : (
                            <div className="animate-in fade-in duration-500">
                                {loading ? (
                                    <div className="flex flex-col items-center justify-center h-80 space-y-6">
                                        <div className="relative">
                                            <div className="w-16 h-16 border-4 border-rose-500 border-t-transparent rounded-full animate-spin" />
                                            <div className="absolute inset-0 flex items-center justify-center">
                                                <span className="material-symbols-outlined text-rose-500 text-[24px] animate-pulse">kitchen</span>
                                            </div>
                                        </div>
                                        <div className="text-center">
                                            <p className="font-black text-slate-800 dark:text-white text-xl">Menü Hazırlanıyor...</p>
                                            <div className="flex flex-col items-center mt-3 gap-1">
                                                <p className="text-xs text-rose-500 font-black uppercase tracking-widest animate-pulse">{activeLoadingStep}</p>
                                                <p className="text-[11px] text-slate-500 dark:text-slate-400 font-bold">
                                                    Şefim size en uygun menüyü hazırlıyoruz.
                                                </p>

                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    isCurrentMealCompleted ? (
                                        <div className="col-span-full py-20 text-center">
                                            <div className="size-20 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mx-auto mb-6">
                                                <span className="material-symbols-outlined text-[40px] text-emerald-600 dark:text-emerald-400">task_alt</span>
                                            </div>
                                            <h3 className="text-2xl font-black text-slate-800 dark:text-white">Şefim, {currentMealLabel.toLocaleLowerCase('tr-TR')} öğününü tamamladınız.</h3>
                                            <p className="text-slate-500 dark:text-slate-400 mt-2 max-w-xl mx-auto font-bold">
                                                İstersen günün diğer öğünleri için tekrar öneri alabilirsin.
                                            </p>
                                        </div>
                                    ) : (
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                                        {recipes.find(r => r.lunchboxMessage) && (
                                            <div className="col-span-full mb-4 p-5 rounded-[2rem] bg-gradient-to-br from-blue-600 to-indigo-700 text-white shadow-xl border border-blue-400/50 flex items-center gap-6 animate-in slide-in-from-top-4 duration-500">
                                                <div className="size-16 rounded-2xl bg-white/20 backdrop-blur-xl flex items-center justify-center shrink-0 border border-white/30 shadow-inner">
                                                    <span className="material-symbols-outlined text-[32px] text-white">backpack</span>
                                                </div>
                                                <div className="flex-1">
                                                    <p className="text-xs font-black uppercase tracking-[0.2em] opacity-80 mb-1 text-blue-100">Beslenme Çantası Önerisi</p>
                                                    <p className="text-lg font-bold leading-tight drop-shadow-sm">{recipes.find(r => r.lunchboxMessage).lunchboxMessage}</p>
                                                </div>
                                                <div className="text-4xl pr-2 grayscale-0 drop-shadow-lg">🍽️</div>
                                            </div>
                                        )}
                                        {(() => {
                                            if (structuredMenu) {
                                                const { main, pair, soup, salad, dessert } = structuredMenu;
                                                const pairings = Array.isArray(pair) ? pair.filter(Boolean) : [];
                                                const hasPairings = pairings.length > 0;
                                                const orderedItems = [
                                                    { id: 'soup', data: soup, label: "ÇORBA" },
                                                    { id: 'main', data: main, label: hasPairings ? "ANA YEMEK + EŞLİKÇİ" : "ANA YEMEK", pairings },
                                                    { id: 'salad', data: salad, label: "SALATA" },
                                                    { id: 'dessert', data: dessert, label: "TATLI" }
                                                ];

                                                return (
                                                    <React.Fragment>
                                                        {orderedItems.map((item) => {
                                                            if (!selectedCategories.includes(item.id) || !item.data) return null;

                                                            const recipeWithPairs = item.id === 'main'
                                                                ? { ...item.data, suggestedPairings: item.pairings }
                                                                : item.data;

                                                            return (
                                                                <div key={item.id} className={`${item.id === 'main' && hasPairings ? 'lg:col-span-2' : 'lg:col-span-1'} flex flex-col h-full`}>
                                                                    <div className="mb-3 px-2 flex-none">
                                                                        <span className="px-3 py-1 bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 text-[10px] font-black rounded-lg uppercase tracking-widest border border-slate-200 dark:border-slate-700">
                                                                            {item.label}
                                                                        </span>
                                                                    </div>
                                                                    <div className="flex-1">
                                                                        <RecipeMenuGroup
                                                                            recipe={recipeWithPairs}
                                                                            isDarkMode={isDarkMode}
                                                                            onCardClick={handleRecipeClick}
                                                                            onToggleSelect={toggleSelection}
                                                                            onSwap={(recipe) => handleSwapStructuredSlot(item.id, recipe)}
                                                                            selectedRecipeNames={selectedRecipeNames}
                                                                            isCompleted={isCurrentMealCompleted}
                                                                            isModern={true}
                                                                            layoutMode={item.id === 'main' && hasPairings ? 'featured' : 'compact'}
                                                                            userProfile={userProfile}
                                                                            userPortion={parseInt(portion)}
                                                                        />
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </React.Fragment>
                                                );
                                            }

                                            // DISCOVERY MODE: Flat list but with standard grouping
                                            if (recipes.length === 0) {
                                                if (gateState === 'meal_slot_disabled') {
                                                    const slotTime = mealSlotActivation?.[currentMealSlot]?.time || null;
                                                    return (
                                                        <div className="col-span-full py-16">
                                                            <div className="max-w-2xl mx-auto rounded-3xl border border-sky-300/60 dark:border-sky-500/40 bg-sky-50 dark:bg-sky-900/20 p-6 text-center">
                                                                <div className="size-16 rounded-2xl bg-sky-100 dark:bg-sky-800/40 flex items-center justify-center mx-auto mb-4">
                                                                    <span className="material-symbols-outlined text-[32px] text-sky-700 dark:text-sky-300">schedule</span>
                                                                </div>
                                                                <h3 className="text-xl font-black text-slate-900 dark:text-sky-100">{currentMealLabel} öğünü şu anda kapalı</h3>
                                                                <p className="text-slate-700 dark:text-sky-200 mt-2 font-semibold">
                                                                    Ankette bu öğün için “yapmıyorum” seçildiği için bu slotta menü üretilmiyor.
                                                                </p>
                                                                {slotTime && (
                                                                    <p className="mt-3 text-sm font-bold text-slate-600 dark:text-sky-200">
                                                                        Kayıtlı slot saati: {slotTime}
                                                                    </p>
                                                                )}
                                                                <button
                                                                    onClick={() => navigate('/profile')}
                                                                    className="mt-5 inline-flex items-center gap-2 rounded-2xl bg-sky-600 px-5 py-3 text-sm font-black text-white shadow-lg shadow-sky-500/20 transition-all hover:bg-sky-700 active:scale-95"
                                                                >
                                                                    <span className="material-symbols-outlined text-[18px]">settings</span>
                                                                    Profilden Düzenle
                                                                </button>
                                                            </div>
                                                        </div>
                                                    );
                                                }
                                                if (gateState === 'breakfast_separate_domain') {
                                                    return (
                                                        <div className="col-span-full py-16">
                                                            <div className="max-w-3xl mx-auto">
                                                                <MenuCompletionBanner
                                                                    isDarkMode={isDarkMode}
                                                                    title="Şefim, afiyet olsun"
                                                                    subtitle="Kahvaltı önerileri ayrı akışta hazırlanıyor."
                                                                    detail="Sabah önerilerini kahvaltı ekranından görebilirsin. Öğle ve akşam için burada yeniden öneri alabilirsin."
                                                                />
                                                            </div>
                                                        </div>
                                                    );
                                                }
                                                if (gateState === 'onboarding_day1_hidden' || blockedReasons.length > 0) {
                                                    return (
                                                        <div className="col-span-full py-16">
                                                            <div className="max-w-2xl mx-auto rounded-3xl border border-amber-300/60 dark:border-amber-500/40 bg-amber-50 dark:bg-amber-900/20 p-6 text-center">
                                                                <div className="size-16 rounded-2xl bg-amber-100 dark:bg-amber-800/40 flex items-center justify-center mx-auto mb-4">
                                                                    <span className="material-symbols-outlined text-[32px] text-amber-700 dark:text-amber-300">lock_clock</span>
                                                                </div>
                                                                <h3 className="text-xl font-black text-slate-900 dark:text-amber-100">Smart Search henüz hazır değil</h3>
                                                                <p className="text-slate-700 dark:text-amber-200 mt-2 font-semibold">
                                                                    Kalan onboarding adımlarını tamamlayınca öneriler burada görünecek.
                                                                </p>
                                                                {blockedReasons.length > 0 && (
                                                                    <div className="mt-4 text-left max-w-md mx-auto">
                                                                        <p className="text-xs font-black tracking-widest uppercase text-slate-500 dark:text-amber-300 mb-2">Kilit Nedenleri</p>
                                                                        <ul className="space-y-1">
                                                                            {blockedReasons.map((reason) => (
                                                                                <li key={reason} className="text-sm font-semibold text-slate-700 dark:text-amber-100">
                                                                                    - {blockedReasonLabels[reason] || reason}
                                                                                </li>
                                                                            ))}
                                                                        </ul>
                                                                    </div>
                                                                )}
                                                                {onboardingGate?.resolvedAt && (
                                                                    <p className="mt-4 text-xs text-slate-500 dark:text-amber-300">
                                                                        Gate: {onboardingGate.gateVersion || 'onboarding-gates-v1'} - {new Date(onboardingGate.resolvedAt).toLocaleString('tr-TR')}
                                                                    </p>
                                                                )}
                                                            </div>
                                                        </div>
                                                    );
                                                }
                                                return (
                                                    <div className="col-span-full py-24 text-center">
                                                        <div className="size-20 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center mx-auto mb-6">
                                                            <span className="material-symbols-outlined text-[40px] text-slate-400">restaurant_menu</span>
                                                        </div>
                                                        <h3 className="text-xl font-bold text-slate-800 dark:text-white">Henüz uygun tarif yok</h3>
                                                        <p className="text-slate-500 dark:text-slate-400 mt-2 max-w-sm mx-auto">Seçtiğiniz kategorilerde elinizdeki malzemelerle yapılabilecek bir tarif bulamadık.</p>
                                                    </div>
                                                );
                                            }

                                            return recipes.map((recipe, idx) => {
                                                const rawIdx = recipe.categoryIndex !== undefined ? recipe.categoryIndex : 1;
                                                const catIdx = Number(rawIdx);
                                                const style = (recipe.serveStyle || '').toLowerCase();
                                                const menuType = String(recipe.menuType || '').toLowerCase();
                                                const slotForSwap =
                                                    menuType === 'soup' || catIdx === 0 || style === 'corba' || style === 'soup'
                                                        ? 'soup'
                                                        : menuType === 'salad' || style === 'salata' || style === 'salad' || style === 'meze'
                                                            ? 'salad'
                                                            : menuType === 'dessert' || catIdx === 3 || style === 'tatli' || style === 'dessert'
                                                                ? 'dessert'
                                                                : 'main';

                                                // Smart filter logic
                                                if (!recipe.isMatrix) {
                                                    const isSoup = selectedCategories.includes('soup') && (catIdx === 0 || style === 'corba' || style === 'soup');
                                                    const isMain = selectedCategories.includes('main') && (catIdx === 1 || isNaN(catIdx) || catIdx === 5 || style === 'yan_yemek' || style === 'ana_yemek' || style === 'hamur_isi' || style === 'hamur_işi' || style === 'pastry');
                                                    const isSalad = selectedCategories.includes('salad') && (style === 'salata' || style === 'salad' || style === 'meze');
                                                    const isDessert = selectedCategories.includes('dessert') && (catIdx === 3 || style === 'tatli' || style === 'dessert');
                                                    if (!isSoup && !isMain && !isSalad && !isDessert) return null;
                                                }

                                                // Matrix section headers
                                                const showHeader = recipe.isMatrix && (idx === 0 || recipes[idx - 1].matrixType !== recipe.matrixType);
                                                const sectionMeta = {
                                                    rescue: { label: 'GÜNÜN KURTARMA PAKETİ', icon: 'savings', gradient: 'from-emerald-600 to-emerald-700', bg: 'bg-emerald-500/10' },
                                                    breakfast: { label: 'GÜNÜN KAHVALTI MENÜSÜ', icon: 'wb_sunny', gradient: 'from-amber-500 to-amber-600', bg: 'bg-amber-500/10' },
                                                    dinner: { label: 'SİZİN İÇİN SEÇİLENLER', icon: 'auto_awesome', gradient: 'from-rose-500 to-rose-600', bg: 'bg-rose-500/10' }
                                                }[recipe.matrixType] || null;

                                                return (
                                                    <React.Fragment key={recipe.id || idx}>
                                                        {showHeader && sectionMeta && (
                                                            <div className="col-span-full mb-2 mt-8 animate-in fade-in slide-in-from-left-4 duration-500 first:mt-0">
                                                                <div className="flex items-center gap-3">
                                                                    <div className={`p-2 rounded-xl ${sectionMeta.bg}`}>
                                                                        <span className={`material-symbols-outlined text-[20px] bg-gradient-to-br ${sectionMeta.gradient} bg-clip-text text-transparent font-black`}>
                                                                            {sectionMeta.icon}
                                                                        </span>
                                                                    </div>
                                                                    <span className={`text-xs font-black uppercase tracking-[0.2em] bg-gradient-to-r ${sectionMeta.gradient} bg-clip-text text-transparent`}>
                                                                        {sectionMeta.label}
                                                                    </span>
                                                                    <div className="flex-1 h-px bg-slate-100 dark:bg-slate-800 ml-4 rounded-full opacity-50" />
                                                                </div>
                                                            </div>
                                                        )}
                                                        <div className={`${recipe.suggestedPairings?.length > 0 ? 'lg:col-span-2' : 'lg:col-span-1'} flex flex-col h-full`}>
                                                            <RecipeMenuGroup
                                                                recipe={recipe}
                                                                isDarkMode={isDarkMode}
                                                                onCardClick={handleRecipeClick}
                                                                onToggleSelect={toggleSelection}
                                                                onSwap={(r) => handleSwapStructuredSlot(slotForSwap, r)}
                                                                selectedRecipeNames={selectedRecipeNames}
                                                                isCompleted={isCurrentMealCompleted}
                                                                isModern={true}
                                                                layoutMode={recipe.suggestedPairings?.length > 0 ? 'browseFeaturedPair' : 'compact'}
                                                                userProfile={userProfile}
                                                                userPortion={parseInt(portion)}
                                                                className="flex-1"
                                                            />
                                                        </div>
                                                    </React.Fragment>
                                                );
                                            });
                                        })()}
                                    </div>
                                    )
                                )}
                            </div>
                        )}

                        {/* Bottom Utility Bar (Pagination & Navigation) */}
                        {!loading && (
                            <div className="mt-12 mb-8 flex flex-col items-center justify-center gap-6 animate-in fade-in slide-in-from-bottom-4 duration-500">

                                {/* Bottom Action Section */}
                                {(recipes.length > 0 || viewMode === 'browse_all') && (
                                    <div className="flex flex-col items-center justify-center gap-4 w-full">
                                        <div className="flex flex-col items-center text-center">
                                            <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1">Seçim Durumu</p>
                                            <div className="flex items-center gap-2 mb-1">
                                                <span className="text-[10px] font-black px-2 py-0.5 bg-slate-100 dark:bg-slate-800 rounded text-slate-600 uppercase">AİLENİZE ÖZEL MENÜLER</span>
                                            </div>
                                            {isCurrentMealCompleted ? (
                                                <p className="text-sm font-black text-slate-500 italic">{currentMealLabel} tamamlandı. Diğer öğün için yeniden öneri alabilirsin.</p>
                                            ) : selectedRecipeNames.length === 0 ? (
                                                <p className="text-sm font-bold text-slate-500 italic">Pişirdiğini seçerek listeyi güncelle...</p>
                                            ) : (
                                                <p className="text-sm font-black text-emerald-600 dark:text-emerald-400">
                                                    {selectedRecipeNames.length} TARİF SEÇİLDİ ✅
                                                </p>
                                            )}
                                        </div>
                                        <button
                                            onClick={handlePrimaryMenuAction}
                                            disabled={isCurrentMealCompleted || isApproving || getCurrentMenuRecipes().length === 0}
                                            className={`flex items-center gap-4 px-10 py-4 rounded-2xl font-black text-base transition-all active:scale-95 shadow-xl ${isApproving
                                                ? 'bg-slate-300 dark:bg-slate-700 cursor-not-allowed text-white'
                                                : (!isCurrentMealCompleted && getCurrentMenuRecipes().length > 0)
                                                    ? 'bg-gradient-to-r from-emerald-500 to-emerald-600 text-white hover:shadow-emerald-500/30 hover:-translate-y-1'
                                                    : 'bg-slate-100 dark:bg-slate-800 text-slate-400 dark:text-slate-600 cursor-not-allowed'
                                                }`}
                                        >
                                            {isApproving ? (
                                                <div className="w-6 h-6 border-3 border-white border-t-transparent rounded-full animate-spin" />
                                            ) : (
                                                <span className="material-symbols-outlined font-bold text-[24px]">skillet</span>
                                            )}
                                            {selectedRecipeNames.length === 0 ? 'TÜM MENÜYÜ SEÇ' : 'MENÜYÜ ONAYLA VE PİŞİR'}
                                        </button>
                                    </div>
                                )}

                                {/* Repositioned "Tüm Tarifler" & Secondary Pagination */}
                                {viewMode === 'smart_menu' && (
                                    <div className="flex items-center gap-4 p-2 bg-slate-50 dark:bg-slate-900/50 rounded-2xl border border-slate-100 dark:border-slate-800 shadow-sm">
                                        <button
                                            onClick={() => handleBrowseAll(selectedCategories, 1)}
                                            className="px-6 py-3 bg-rose-500 hover:bg-rose-600 dark:bg-rose-600 dark:hover:bg-rose-700 text-white rounded-xl font-black text-sm transition-all flex items-center gap-2 shadow-lg shadow-rose-500/20 active:scale-95"
                                        >
                                            <span className="material-symbols-outlined text-[20px]">library_books</span>
                                            Tüm Tarifler
                                        </button>
                                        <div className="w-px h-6 bg-slate-200 dark:bg-slate-700" />
                                        <p className="px-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Daha Fazla Keşfet</p>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>

            </div>

            {
                recipeModalOpen && selectedRecipe && (
                    <RecipeModalGateway
                        modalVariant="smart"
                        onPairingClick={handleRecipeClick}
                        isOpen={recipeModalOpen}
                        onClose={() => setRecipeModalOpen(false)}
                        recipe={selectedRecipe}
                        isDarkMode={isDarkMode}
                        userProfile={selectedRecipe?._liveUserProfile || userProfile}
                        userPortion={parseInt(portion)}
                        skillLevel={skillLevel}
                        availableComponents={availableComponents}
                        onAddToShoppingList={handleAddMissingToShoppingList}
                    />
                )
            }

            {swapModalState.open && (
                <div
                    className="fixed inset-0 z-[210] bg-slate-950/60 backdrop-blur-sm p-2 sm:p-4 overflow-y-auto"
                    onClick={() => setSwapModalState({ open: false, slotId: null, currentRecipe: null, alternatives: [] })}
                >
                    <div
                        className="mx-auto my-4 sm:my-8 w-full max-w-[1760px] rounded-[28px] border border-slate-200 dark:border-slate-700 bg-white dark:bg-[#0f172a] shadow-2xl overflow-hidden"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="sticky top-0 z-10 flex items-start justify-between gap-4 px-4 sm:px-6 py-5 border-b border-slate-200 dark:border-slate-800 bg-white/95 dark:bg-[#0f172a]/95 backdrop-blur">
                            <div>
                                <p className="text-[11px] font-black uppercase tracking-[0.28em] text-rose-500">
                                    Akıllı Alternatifler
                                </p>
                                <h3 className="mt-2 text-2xl font-black text-slate-900 dark:text-white">
                                    {swapModalState.slotId === 'main' ? 'Ana Yemeği Değiştir' : 'Slotu Değiştir'}
                                </h3>
                                <p className="mt-2 text-sm text-slate-500 dark:text-slate-300 max-w-2xl">
                                    Stok, malzeme uyumu, eşlik dengesi ve bütçe akışına göre en uygun seçenekleri senin için öne çıkardık.
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={() => setSwapModalState({ open: false, slotId: null, currentRecipe: null, alternatives: [] })}
                                className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-slate-200 dark:border-slate-700 text-slate-500 hover:text-slate-900 dark:hover:text-white"
                            >
                                <span className="material-symbols-outlined">close</span>
                            </button>
                        </div>

                        <div className="px-3 sm:px-5 lg:px-6 py-4 sm:py-5 max-h-[calc(100dvh-110px)] overflow-y-auto">
                            <div className="grid gap-4 sm:gap-5 grid-cols-1 md:grid-cols-2 xl:grid-cols-4">
                                {swapModalState.alternatives.map((recipe) => {
                                    const recipeName = recipe?.name || recipe?.title || 'Tarif';
                                    return (
                                        <div
                                            key={String(recipe?.id || recipeName)}
                                            className="rounded-[28px] border border-slate-200 dark:border-slate-700 bg-slate-50/80 dark:bg-slate-900/60 p-3"
                                        >
                                            <RecipeCard
                                                recipe={recipe}
                                                onCardClick={handleRecipeClick}
                                                isDarkMode={isDarkMode}
                                                userProfile={userProfile}
                                                userPortion={parseInt(portion, 10) || 4}
                                                hideActionButtons={true}
                                                hideSavingsPanel={true}
                                                hideCostComparison={true}
                                                showTopInsight={false}
                                            />

                                            <p className="mt-3 px-1 text-sm text-slate-500 dark:text-slate-300 leading-relaxed">
                                                Bu seçeneği alırsan sistem ana yemek akışını ve eşlikleri buna göre korumaya çalışacak.
                                            </p>

                                            <div className="mt-4 px-1">
                                                <button
                                                    type="button"
                                                    onClick={() => applyStructuredSlotReplacement(swapModalState.slotId, swapModalState.currentRecipe, recipe)}
                                                    className="w-full rounded-2xl bg-rose-500 px-4 py-3 text-sm font-black text-white transition-all hover:bg-rose-600"
                                                >
                                                    Bu Tarifi Seç
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <HealthPlusActivationModal
                isOpen={showHealthPlusModal}
                onClose={() => setShowHealthPlusModal(false)}
                onProceedToPayment={handleHealthPlusProceedToPayment}
            />

            <PremiumPurchaseModal
                isOpen={showHealthPlusPaymentModal}
                onClose={async (reason) => {
                    if (reason === 'open') {
                        setShowHealthPlusPaymentModal(true);
                        return;
                    }
                    setShowHealthPlusPaymentModal(false);
                    if (reason === 'verified') {
                        setHealthPlusEntitled(true);
                        await setHealthPlusActivationState(true);
                        return;
                    }
                    fetchHealthPlusState();
                }}
                preferredModuleKey="global"
                preferredModuleTitle="Sağlık+ Premium"
            />
            <BabyModal
                isOpen={forceOnboardingBabyModal || (showBabyModal && !!pendingRecipeForOpen)}
                onClose={handleBabyModalClose}
                onSubmit={handleBabyModalSubmit}
            />
        </DashboardLayout>
    );
}
